import { useState } from "react";
import { Button } from "@legal-500/legal500-design-system";
import { powerlistEditions, type Edition } from "../../api/powerlist.service";
import { usePowerlistAccess } from "./shared";
import styles from "./Powerlist.module.css";

type Transition = "publish" | "unpublish" | "archive";

const ACTIONS: Record<Edition["status"], Transition[]> = {
  draft: ["publish"],
  published: ["unpublish", "archive"],
  archived: ["unpublish"],
};

const LABELS: Record<Transition, string> = {
  publish: "Publish",
  unpublish: "Unpublish",
  archive: "Archive",
};

type EditionPublishActionsProps = {
  edition: Edition;
  onTransitioned: (edition: Edition, message: string) => void;
  onError: (error: unknown) => void;
};

export function EditionPublishActions({
  edition,
  onTransitioned,
  onError,
}: EditionPublishActionsProps) {
  const { canPublish } = usePowerlistAccess();
  const [busy, setBusy] = useState(false);

  if (!canPublish) return null;

  async function run(action: Transition) {
    setBusy(true);
    try {
      const result = await powerlistEditions.transition(edition.id, action);
      const message =
        action === "publish"
          ? `${result.edition.title} published with ${result.entriesPublished} draft ${result.entriesPublished === 1 ? "entry" : "entries"}`
          : `${result.edition.title} ${action === "archive" ? "archived" : "moved back to draft"}`;
      onTransitioned(result.edition, message);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.publishBar}>
      <div className={styles.actions}>
        {ACTIONS[edition.status].map(action => (
          <Button
            key={action}
            variant={action === "publish" ? "contained" : "outlined"}
            color={action === "publish" ? "primary" : "default"}
            size="small"
            disabled={busy}
            onClick={() => void run(action)}
          >
            {LABELS[action]}
          </Button>
        ))}
      </div>
      <p className={styles.publishNote}>The public site updates within a minute</p>
    </div>
  );
}
